/**
 * Product Gallery Video
 *
 * Plays Shopify-hosted videos in the PDP media gallery and pauses them again
 * when the shopper navigates/scrolls away:
 *
 *  - Carousel galleries (slider mode + the mobile carousel of grid mode):
 *    the active slide drives playback. We listen for the tarot carousel's
 *    `render-index:changed` event and play the active slide's video, pausing
 *    the rest. An IntersectionObserver on the carousel pauses everything when
 *    the gallery scrolls out of the viewport and resumes the active video when
 *    it returns.
 *
 *  - Desktop grid galleries (grid-1 / grid-2): there is no carousel, so each
 *    video gets its own IntersectionObserver and plays when it scrolls into
 *    view, pausing when it leaves.
 *
 * Video slides/cells are marked with data-media-type="video" in Liquid. The
 * <video> shows its native poster (from image_size) until it plays, so there is
 * no separate poster element to manage here.
 */

/**
 * Try to play with sound. Browsers block autoplay-with-sound until the user has
 * interacted with the page, so if the first attempt is rejected we retry muted
 * (the native controls let the shopper unmute with one click). `forceMuted`
 * skips straight to muted — used in the desktop grid where several videos can be
 * on screen at once and overlapping audio would be jarring.
 */
function playVideo(video, { forceMuted = false } = {}) {
  if (!video) return;
  if (forceMuted) video.muted = true;

  video.play().catch(() => {
    video.muted = true;
    video.play().catch(() => {});
  });
}

function pauseVideo(video) {
  if (!video) return;
  video.pause();
  video.currentTime = 0;
}

/**
 * Drives the videos inside a single `<tarot-carousel>` gallery.
 */
class GalleryCarouselVideos {
  constructor(carousel) {
    this.carousel = carousel;
    this.slides = Array.from(carousel.querySelectorAll("tarot-slide"));
    this.currentIndex = 0;
    this.isVisible = false;
  }

  init() {
    // Nothing to do if this gallery has no videos.
    if (!this.carousel.querySelector("video")) return;

    if (typeof this.carousel.on === "function") {
      this.carousel.on("render-index:changed", (data) => {
        this.currentIndex = data.currentIndex ?? data.newIndex ?? 0;
        if (this.isVisible) this.activate(this.currentIndex);
      });
    }

    // Pause when the gallery leaves the viewport; (re)start the active video
    // when it comes back. A display:none carousel (e.g. the mobile carousel on
    // desktop) reports as not-intersecting, so it never plays — no duplicate
    // playback with the desktop grid.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting;
          if (this.isVisible) {
            this.activate(this.currentIndex);
          } else {
            this.pauseAll();
          }
        });
      },
      { threshold: 0.3 },
    );
    observer.observe(this.carousel);
  }

  videoAt(index) {
    const slide = this.slides[index];
    return slide ? slide.querySelector("video") : null;
  }

  activate(index) {
    this.pauseAll();
    playVideo(this.videoAt(index));
  }

  pauseAll() {
    this.slides.forEach((slide) => pauseVideo(slide.querySelector("video")));
  }
}

/**
 * Per-video play-on-scroll for the desktop grid (videos not inside a carousel).
 */
function setupGridVideos() {
  const videos = Array.from(
    document.querySelectorAll('[data-media-type="video"] video'),
  ).filter((video) => !video.closest("tarot-carousel"));

  if (!videos.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          playVideo(entry.target, { forceMuted: true });
        } else {
          pauseVideo(entry.target);
        }
      });
    },
    { threshold: 0.5 },
  );

  videos.forEach((video) => observer.observe(video));
}

function init() {
  document
    .querySelectorAll('tarot-carousel[id^="main-carousel-"]')
    .forEach((carousel) => new GalleryCarouselVideos(carousel).init());

  setupGridVideos();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

export { init, GalleryCarouselVideos };
